import { useMemo } from 'react';

import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';

import { LoanForm as DispositionsKreditForm } from './CreditKinds/DispositionsKredit/LoanForm';
import { LoanForm as GeschaeftsKreditForm } from './CreditKinds/GeschaeftsKredit/LoanForm';
import { LoanForm as ImmobiliendarlehenForm } from './CreditKinds/Immobiliendarlehen/LoanForm';
import { LoanForm as KontokorrentKreditForm } from './CreditKinds/KontokorrentKredit/LoanForm';
import { LoanForm as KreditKarteForm } from './CreditKinds/KreditKarte/LoanForm';
import { LoanForm as LeasingForm } from './CreditKinds/Leasing/LoanForm';
import { LoanForm as RatenKreditForm } from './CreditKinds/RatenKredit/LoanForm';
import { CreditFormCard } from './CreditKinds/shared/CreditFormCard';
import { LoanForm as SonstigeVerbindlichkeitForm } from './CreditKinds/SonstigeVerbindlichkeit/LoanForm';
import { useExistingLoanActions } from './hooks/useExistingLoanActions';
import { getCommandName } from './utils/getCommandName';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaExistingLoans } from '~/features/leadDetails/schemas/existingLoans.schema';
import { canExecuteForAttribution } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    creditType: keyof SchemaExistingLoans;
    lead: SchemaLoanRequestResolver_Get;
    handleSaveCredit: () => void;
    handleCancel: () => void;
};

type FormContentProps = {
    creditType: keyof SchemaExistingLoans;
    creditIndex: number;
    applicant: APPLICANT_ATTRIBUTION_BOTH_TYPE;
};

const FormContent: React.FC<FormContentProps> = ({ applicant, creditIndex, creditType }) => {
    switch (creditType) {
        case 'ratenkredite':
            return <RatenKreditForm index={creditIndex} applicant={applicant} />;
        case 'geschaeftskredite':
            return <GeschaeftsKreditForm index={creditIndex} applicant={applicant} />;
        case 'kreditkarten':
            return <KreditKarteForm index={creditIndex} applicant={applicant} />;
        case 'leasings':
            return <LeasingForm index={creditIndex} applicant={applicant} />;
        case 'kontokorrentkredite':
            return <KontokorrentKreditForm index={creditIndex} applicant={applicant} />;
        case 'sonstigeVerbindlichkeiten':
            return <SonstigeVerbindlichkeitForm index={creditIndex} applicant={applicant} />;
        case 'dispositionskredite':
            return <DispositionsKreditForm index={creditIndex} applicant={applicant} />;
        case 'immobiliendarlehen':
            return <ImmobiliendarlehenForm index={creditIndex} applicant={applicant} />;
        default:
            return null;
    }
};

export const AppendCreditForm: React.FC<Props> = ({ creditType, handleCancel, handleSaveCredit, lead }) => {
    const { t } = useTranslation();
    const { control, getValues } = useFormContext<SchemaCreditRequestForm>();
    const { permissions } = useAclPermissions({ loanRequestId: lead.id });
    const {
        addMutation: { mutate: addExistingLoan },
    } = useExistingLoanActions({
        loanRequestId: lead.id,
    });
    const watchedCreditType = useWatch({ control, name: creditType });
    const creditTypeValues = useMemo(() => watchedCreditType ?? [], [watchedCreditType]);
    const newCreditIndex = creditTypeValues.findIndex((credit) => credit.id === '');
    const applicant = creditTypeValues[newCreditIndex]?.gehoertZuAntragsteller;

    if (newCreditIndex === -1 || !applicant) return null;

    const canAdd = canExecuteForAttribution({
        attribution: applicant,
        commandName: getCommandName({ action: 'add', loanType: creditType }),
        permissions,
    });

    const handleSave = () => {
        const currentCredit = getValues(`${creditType}.${newCreditIndex}`);
        addExistingLoan(
            { loanType: creditType, payload: { createdAt: new Date(), ...currentCredit } },
            { onSuccess: handleSaveCredit },
        );
    };

    return (
        <CreditFormCard
            title={t(`EXISTING_LOANS.TYPES.${creditType}`)}
            actions={
                <>
                    <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuCircleX />} onClick={handleCancel}>
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button iconLeft={<LuSave />} onClick={handleSave} disabled={!canAdd}>
                        {t('BUTTONS.SAVE')}
                    </Button>
                </>
            }
        >
            <FormContent creditIndex={newCreditIndex} creditType={creditType} applicant={applicant} />
        </CreditFormCard>
    );
};
